import { useMutation } from '@apollo/client';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  ImportExternalProjectInput,
  ImportExternalProjectResponse,
} from '@provision/entity-types';
import { useCallback, useState } from 'react';
import { DtoolsProjectsList } from 'src/components/ImportProposalsModal/dtools-projects-list';
import { ImportProposalsSearch } from 'src/components/ImportProposalsModal/import-proposals-search';
import { IMPORT_DTOOLS_PROJECT } from 'src/graphql/entities/integrations/dtools/dtools.operations';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { ImportResults } from './import-results';
import { ProjectsSummary } from './projects-summary';

export const ImportDtoolsProjectModal = () => {
  const { closeModal } = useModalMethods(Modals.ImportDtoolsProjects);
  const { showErrorAlert } = useNotifications();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const [currentView, setCurrentView] = useState<
    'SELECT_ITEMS_VIEW' | 'PREVIEW' | 'COMPLETED'
  >('SELECT_ITEMS_VIEW');
  const [searchValue, setSearchValue] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<
    { id: number | string; name: string }[]
  >([]);

  const selectedCount = selectedProjects.length;
  const selectedLabel = selectedCount
    ? `${selectedCount} ${selectedCount === 1 ? 'project' : 'projects'} selected`
    : 'No projects selected';

  const [importDtoolsProject, importDtoolsProjectResult] = useMutation<
    { importDtoolsProject: ImportExternalProjectResponse<string> },
    ImportExternalProjectInput<string>
  >(IMPORT_DTOOLS_PROJECT, {
    variables: {
      params: {
        projectIds: selectedProjects.map((p) => p.id.toString()),
        partnerId: selectedPartnerId!,
      },
    },
    onError: (error) => {
      showErrorAlert(error.message);
    },
  });

  const handleOnImport = useCallback(async () => {
    if (!selectedPartnerId || selectedProjects.length === 0) {
      return;
    }
    await importDtoolsProject();
    setCurrentView('COMPLETED');
  }, [importDtoolsProject, selectedPartnerId, selectedProjects.length]);

  if (currentView === 'SELECT_ITEMS_VIEW') {
    return (
      <Dialog
        open
        onClose={closeModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6">Import from D-Tools Cloud</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Select projects to import
            </Typography>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Stack spacing={2}>
            <ImportProposalsSearch
              onChange={setSearchValue}
              placeholder="Search projects..."
            />
            <DtoolsProjectsList
              filterText={searchValue}
              onProjectsSelected={setSelectedProjects}
              showSelectAll
              initialSelectedIds={selectedProjects.map((p) => p.id.toString())}
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedLabel}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => setCurrentView('PREVIEW')}
              disabled={selectedProjects.length === 0}
            >
              {`Review ${selectedProjects.length} ${selectedProjects.length === 1 ? 'item' : 'items'} →`}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    );
  }

  if (currentView === 'PREVIEW') {
    return (
      <ProjectsSummary
        projects={selectedProjects}
        onCancelClick={closeModal}
        onBackClick={() => setCurrentView('SELECT_ITEMS_VIEW')}
        onImportClick={handleOnImport}
        disableImportButton={importDtoolsProjectResult.loading}
      />
    );
  }

  return (
    <Dialog
      open
      onClose={closeModal}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.secondary">
            Import Complete
          </Typography>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2 }}>
        <ImportResults
          records={importDtoolsProjectResult.data?.importDtoolsProject.records ?? []}
          loading={importDtoolsProjectResult.loading}
          projectIds={selectedProjects.map((p) => p.id.toString())}
          errors={importDtoolsProjectResult.data?.importDtoolsProject.errors}
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={() => setCurrentView('SELECT_ITEMS_VIEW')}>Import More</Button>
        <Button variant="contained" onClick={closeModal}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
